import { useEffect } from 'react';
import { kueskiAttribution, tracker } from '@kueski-dev/web-observability';
import { refreshCustomerLendingProfileSettingsOnWindowFocus } from 'Services/CustomerProfile/settings';
import { useConfig } from 'Contexts/Config';
import { useSessionId } from 'Hooks/useSessionId';
import { getVariable } from 'Utils/env';
import { APPLICATION_REFERRER } from 'Src/consts/constants';
import { identify as identifyLD } from 'Services/LaunchDarkly';
import FeatureFlagService from 'Services/FeatureFlags';
import 'Src/observability';
import { saveReferrerInStoreInLocalStorage } from 'Utils/referrerInStoreHelp';
import { useValidateSession } from './useValidateSession';
import { useCustomerId } from './useCustomerId';

/**
 * Execute Global Events/Event Listeners on Aurora App, for example, execute something upon user
 * login, or as soon as we have application info.
 */
export const useInitializers = () => {
  const sessionId = useSessionId();
  const customerId = useCustomerId();
  const isValidSession = useValidateSession();
  const { features, featureActions } = useConfig();

  useEffect(() => {
    tracker.enabled = features.enableObservabilityTracking;
  }, [features.enableObservabilityTracking]);

  useEffect(() => {
    kueskiAttribution.domain = getVariable('COOKIE_DOMAIN') || '';
    if (isValidSession) {
      kueskiAttribution.update();
    }

    // Store external referrer in sessionStorage (including source param)
    const { referrer } = document;
    const { origin } = window.location;
    const referrerInParams = new URLSearchParams(window.location.search).get('source');

    if (!referrer.includes(origin)) {
      sessionStorage.setItem(APPLICATION_REFERRER, referrer);
      saveReferrerInStoreInLocalStorage(referrer);
    }
    if (referrerInParams) {
      sessionStorage.setItem(APPLICATION_REFERRER, referrerInParams);
      saveReferrerInStoreInLocalStorage(referrerInParams);
    }
  }, [isValidSession]);

  // Executed only once, as soon as the user has a session (login or registration)
  useEffect(() => {
    if (!sessionId) return;

    // Refresh Servicing Settings on every window focus
    refreshCustomerLendingProfileSettingsOnWindowFocus();
  }, [sessionId]);

  // Executed only once per application or user data, as soon as the application data is stored
  useEffect(() => {
    if (!customerId) return;

    // Identify the user on LaunchDarkly
    identifyLD(customerId)
      .then(() => FeatureFlagService.refreshFeatureFlags(false, new Set(['LAUNCH_DARKLY_FF'])))
      .then(() => { featureActions.refreshFeatureFlags(true); });
  }, [customerId]);
};
